import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { shoppingGuideLinks } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".guide-dropdown-trigger")) {
        setGuideOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <span className={`font-heading text-xl md:text-2xl transition-colors duration-300 ${
              scrolled ? "text-foreground-900" : "text-white"
            }`}>
              Sugargoo<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
            }`}>
              Home
            </Link>

            <div className="relative guide-dropdown-trigger">
              <button
                onClick={(e) => { e.stopPropagation(); setGuideOpen(!guideOpen); }}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
                }`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-50">
                  <div className="py-2">
                    {shoppingGuideLinks.map((link, i) => (
                      <Link
                        key={i}
                        to={link.slug}
                        onClick={() => setGuideOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className={`${link.icon} text-base text-foreground-400`}></i>
                        {link.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a href="#categories" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
            }`}>
              Categories
            </a>
            <a href="#guides" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
            }`}>
              Guides
            </a>
            <a href="#articles" className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              scrolled ? "text-foreground-700 hover:text-primary-500" : "text-white/90 hover:text-white"
            }`}>
              Articles
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold px-5 py-2 rounded-full transition-all duration-300 whitespace-nowrap ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm border border-white/30"
              }`}
            >
              Buy It Now
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? "text-foreground-800" : "text-white"
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[70vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link to="/" className="text-sm font-medium text-foreground-700 py-2.5 whitespace-nowrap hover:text-primary-500 transition-colors" onClick={() => setMobileOpen(false)}>
              <i className="ri-home-line mr-2 text-foreground-400"></i>
              Home
            </Link>

            <div>
              <button
                onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                className="w-full flex items-center justify-between text-sm font-medium text-foreground-700 py-2.5 whitespace-nowrap hover:text-primary-500 transition-colors cursor-pointer"
              >
                <span>
                  <i className="ri-book-open-line mr-2 text-foreground-400"></i>
                  Shopping Guide
                </span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${mobileGuideOpen ? "rotate-180" : ""}`}></i>
              </button>
              {mobileGuideOpen && (
                <div className="ml-6 border-l-2 border-background-200/70 pl-4 py-1 space-y-1">
                  {shoppingGuideLinks.map((link, i) => (
                    <Link
                      key={i}
                      to={link.slug}
                      onClick={() => { setMobileOpen(false); setMobileGuideOpen(false); }}
                      className="flex items-center gap-2 text-sm text-foreground-600 py-1.5 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <i className={`${link.icon} text-xs text-foreground-400`}></i>
                      {link.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/50 my-2"></div>

            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-1 py-1">Categories</p>
            {categories.map((cat, i) => (
              <a
                key={i}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 text-sm text-foreground-600 py-1.5 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <i className={`${cat.icon} text-xs text-foreground-400`}></i>
                {cat.name}
              </a>
            ))}

            <div className="border-t border-background-200/50 my-2"></div>

            <a href="#guides" className="text-sm font-medium text-foreground-700 py-2.5 whitespace-nowrap hover:text-primary-500 transition-colors" onClick={() => setMobileOpen(false)}>
              <i className="ri-compass-line mr-2 text-foreground-400"></i>
              Guides
            </a>
            <a href="#articles" className="text-sm font-medium text-foreground-700 py-2.5 whitespace-nowrap hover:text-primary-500 transition-colors" onClick={() => setMobileOpen(false)}>
              <i className="ri-article-line mr-2 text-foreground-400"></i>
              Articles
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 text-sm font-semibold text-center bg-primary-500 text-white px-5 py-3 rounded-full whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}